/**
 * Firebase Module Index
 *
 * This file organizes Firebase functions by business domain.
 * Currently re-exports from firebase.ts for backward compatibility.
 * Functions will be gradually migrated to domain-specific modules.
 *
 * Domain modules:
 * - types.ts      - Shared types and utilities (✅ typed)
 * - workingHours.ts - Working hours management (✅ typed)
 * - sales.ts      - Sales operations (planned)
 * - products.ts   - Product management (planned)
 * - clients.ts    - Client management (planned)
 * - inventory.ts  - Supplies, transfers, writeoffs, revisions (planned)
 * - stats.ts      - Daily/monthly statistics (planned)
 * - transactions.ts - Cashbox transactions (planned)
 * - spendings.ts  - Spending management (planned)
 * - stores.ts     - Store configuration (planned)
 * - complexSales.ts - Complex sales (orders) (planned)
 * - auth.ts       - Authentication (planned)
 */

// ============================================================================
// Type exports (properly typed)
// ============================================================================
export * from './types'

// ============================================================================
// Sales Domain (typed module)
// ============================================================================
export type {
  PaymentMethod,
  PurchaseMethod,
  SaleClient,
  CartItem,
  FiscalInfo,
  ReturnInfo,
  Sale,
  SalesByDate,
  GetSalesParams,
  ClientStatistics,
  ProductStats,
  DailyStatIncrements,
} from './sales'

// Export typed implementations (will gradually replace firebase.ts exports)
export {
  dailyStatIncrementsForSales as dailyStatIncrementsForSalesTyped,
  dailyStatIncrementsForReturns as dailyStatIncrementsForReturnsTyped,
} from './sales'

// ============================================================================
// Products Domain (typed module)
// ============================================================================
export type {
  ProductIngredient,
  StorePrice,
  ProductCategory,
  ProductSupplier,
  Product,
  ProductMovement,
  MovementsByDate,
  CreateProductParams,
  UpdateProductParams,
} from './products'

// ============================================================================
// Clients Domain (typed module)
// ============================================================================
export type {
  Client,
  ClientsByDate,
  ClientOperation,
  CreateClientParams,
  UpdateClientParams,
} from './clients'

// ============================================================================
// Inventory Domain (typed module)
// ============================================================================
export type {
  SupplyItem,
  Supply,
  TransferItem,
  Transfer,
  WriteoffItem,
  WriteoffReason,
  Writeoff,
  RevisionItem,
  Revision,
  GroupedByDate,
} from './inventory'

// ============================================================================
// Working Hours (already split out)
// ============================================================================
export {
  dailyStatIncrementsForWorkingHours,
  monthlyStatIncrementsForWorkingHours,
  saveWorkingHours,
  deleteWorkingHours,
  getWorkingHoursOnDateRange,
  getMonthlyWorkingHoursOnDateRange,
  saveNewWorkingHourPaymentTransaction,
  deleteWorkingHourPaymentTransaction,
  recalculateUserSalaryAmounts,
  countEmployeeBonuses,
  createAutomaticWorkingHours,
} from './workingHours'

// ============================================================================
// Firebase Core (from firebase.ts)
// ============================================================================
export {
  // Firebase primitives
  functions,
  auth,
  storage,
  rootDb,
  fieldValue,
  fieldPath,
  emailAuthProvider,
  getRemoteConfigValue,

  // Cloud Functions
  sendMessageToClient,
  createNewInternalUser,
  updateUserInAuth,
  disableUser,
  enableUser,
  deleteUsersFromAuth,

  // Auth
  loginWithEmail,
  logout,
  passwordReset,
  createNewUserAndOrganization,
  checkEmailAvailability,

  // Aggregation
  countClientOrdersFromServer,
  getUniversalAggregation,

  // Sales
  getSalesOnDateRange,
  getClientSalesPaginated,
  getClientStatisticsAggregated,
  getClientTopProductsAggregated,
  getClientSalesDateRange,
  dailyStatIncrementsForSales,
  dailyStatIncrementsForComplexSales,
  dailyStatIncrementsForReturns,
  saveNewSale,
  updateSale,
  markSalePaid,
  updateSaleWithStats,
  deleteSale,
  saveSaleReturn,
  deleteSaleReturn,
  getReturnsOnDateRange,
  getCartItemsByProduct,

  // Products
  saveNewProduct,
  updateProduct,
  uploadProductImage,
  deleteProductImage,
  batchUpdateProducts,
  deleteProduct,
  getProductMovementOnDateRange,
  updateStockManually,
  updateMovement,
  deleteMovement,
  getProductsMovementOnDateRange,

  // Product Categories
  saveNewProductCategory,
  updateProductCategory,
  saveProductCategoriesOrder,
  deleteProductCategory,

  // Suppliers
  saveNewSupplier,
  updateSupplier,
  deleteSupplier,

  // Supplies
  getSuppliesOnDateRange,
  saveNewSupply,
  uploadSupplyInvoice,
  deleteSupplyInvoice,
  uploadPrintTemplateImage,
  deletePrintTemplateImage,
  updateSupply,
  deleteSupply,

  // WriteOffs
  getWriteOffsOnDateRange,
  saveNewWriteOff,
  deleteWriteOff,

  // Revisions
  getRevisionsOnDateRange,
  saveNewRevision,
  updateRevision,
  deleteRevision,
  saveNewPricingRevision,

  // Spendings
  getSpendingsOnDateRange,
  saveNewSpending,
  dailyStatIncrementsForSpendings,
  deleteSpending,

  // Cashbox Transactions
  dailyStatIncrementsForCashboxTransactions,
  saveCashboxTransaction,
  editCashboxShiftStats,
  cancelShiftClosing,
  getCashboxTransactionsOnDateRange,
  deleteCashboxTransaction,

  // Stats
  getStatsForDays,
  getMonthlyStatsOnDateRange,
  getProductStatsForDays,
  getProductReportForDays,

  // Saved Carts
  saveSavedCart,
  deleteSavedCart,
  updatePrintChecks,
  updateSavedCart,

  // Product Tags
  saveProductTag,
  uploadProductTagImage,

  // Transfers
  getTransfersOnDateRange,
  saveNewTransfer,
  deleteTransfer,

  // Checklists
  getChecklist,
  getLatestChecklistBeforeDate,
  updateChecklist,
  addChecklist,

  // Clients
  findClientByPhone,
  addNewClient,
  deleteClient,
  getClientsRegisteredOnDateRange,
  getClientsActiveOnDateRange,
  updateBonusForClient,
  getClientBonusValue,
  getClientOperations,
  updateClient,

  // Stores
  saveNewStore,
  saveWallets,
  deleteWallet,
  savePaymentMethods,
  deletePaymentMethod,
  savePurchaseMethods,
  saveSaleSettings,
  deletePurchaseMethod,
  saveProductModifiers,
  deleteProductModifiers,
  saveRoles,
  deleteRole,
  saveProductUnits,
  deleteProductUnit,
  saveRecurringSpendings,
  deleteRecurringSpending,
  checkIdUniquenes,
  updateLoyaltySettings,
  updateMarketplaceSettings,
  updateStore,
  deleteStore,
  updateStoreTimezone,
  updateStoreWiponCredentials,

  // Marketing
  saveNewMarketingList,

  // Transactions
  getTransactionsOnDateRange,
  getComplexSaleTransactionsOnDateRange,
  saveNewTransaction,
  deleteTransaction,

  // Complex Sales
  getComplexSalesOnDateRange,
  saveNewComplexSale,
  deleteComplexSale,
  saveUpdatedItemsForComplexSale,
  saveUpdatedFinalPriceForComplexSale,
  updateComplexSale,
  addComplexSalePayment,
  deleteComplexSalePayment,
  updateComplexSaleStatus,
  addComplexSaleComment,
  deleteComplexSaleComment,

  // Complex Sale Templates
  saveNewComplexSaleTemplate,
  deleteComplexSaleTemplate,
  updateComplexSaleTemplate,

  // Users
  updateUser,
  updateUserStore,
  updateUserLocale,

  // Organization
  updateOrganization,
  updateOnboardingChecklist,
  completeOnboarding,
  updateConstantsData,

  // Client Orders
  updateClientOrder,

  // Subscription writes are server-authoritative since ATL-855 — see
  // global/Firebase/organizationSubscription.ts. The former `updateSubscription` helper was
  // removed: it resolved through `getFirestoreRef(ref, 'subscriptions')` and therefore wrote
  // `organizations/{org}/subscriptions/{orgId}`, never the organization document it claimed to.

  // Data Management
  deleteDocumentsInBatches,
  resetCollectionData,
  resetMovementData,
  resetProductStocks,
  deleteAllProducts,

  // Manufactures
  getManufacturesOnDateRange,
  saveNewManufacture,
  deleteManufacture,

  // Product Stock
  getProductStockSnapshot,

  // Supply Orders
  saveNewSupplyOrder,
  updateSupplyOrder,
  deleteSupplyOrder,
  getActiveSupplyOrders,

  // Print Templates
  savePrintTemplate,
  deletePrintTemplate,
  getPrintTemplates,
  getPrintTemplatesByTypeAndStore,

  // Price Types
  savePriceTypes,
  addPriceType,
  updatePriceType,
  deletePriceType,
} from './firebase'


// ============================================================================
// Domain-specific module type definitions
// For future use when migrating functions to typed modules
// NOTE: These types use typeof import() which ESLint doesn't like.
// They're kept for documentation purposes but disabled.
// ============================================================================

/* eslint-disable @typescript-eslint/consistent-type-imports */

/**
 * Sales Domain Functions
 * Target: sales.ts
 */
export type SalesFunctions = {
  getSalesOnDateRange: typeof import('./firebase').getSalesOnDateRange
  getClientSalesPaginated: typeof import('./firebase').getClientSalesPaginated
  getClientStatisticsAggregated: typeof import('./firebase').getClientStatisticsAggregated
  getClientTopProductsAggregated: typeof import('./firebase').getClientTopProductsAggregated
  getClientSalesDateRange: typeof import('./firebase').getClientSalesDateRange
  dailyStatIncrementsForSales: typeof import('./firebase').dailyStatIncrementsForSales
  dailyStatIncrementsForComplexSales: typeof import('./firebase').dailyStatIncrementsForComplexSales
  dailyStatIncrementsForReturns: typeof import('./firebase').dailyStatIncrementsForReturns
  saveNewSale: typeof import('./firebase').saveNewSale
  updateSale: typeof import('./firebase').updateSale
  markSalePaid: typeof import('./firebase').markSalePaid
  updateSaleWithStats: typeof import('./firebase').updateSaleWithStats
  deleteSale: typeof import('./firebase').deleteSale
  saveSaleReturn: typeof import('./firebase').saveSaleReturn
  deleteSaleReturn: typeof import('./firebase').deleteSaleReturn
  getReturnsOnDateRange: typeof import('./firebase').getReturnsOnDateRange
  getCartItemsByProduct: typeof import('./firebase').getCartItemsByProduct
}

/**
 * Products Domain Functions
 * Target: products.ts
 */
export type ProductsFunctions = {
  saveNewProduct: typeof import('./firebase').saveNewProduct
  updateProduct: typeof import('./firebase').updateProduct
  uploadProductImage: typeof import('./firebase').uploadProductImage
  deleteProductImage: typeof import('./firebase').deleteProductImage
  batchUpdateProducts: typeof import('./firebase').batchUpdateProducts
  deleteProduct: typeof import('./firebase').deleteProduct
  getProductMovementOnDateRange: typeof import('./firebase').getProductMovementOnDateRange
  updateStockManually: typeof import('./firebase').updateStockManually
  updateMovement: typeof import('./firebase').updateMovement
  deleteMovement: typeof import('./firebase').deleteMovement
  getProductsMovementOnDateRange: typeof import('./firebase').getProductsMovementOnDateRange
}

/**
 * Clients Domain Functions
 * Target: clients.ts
 */
export type ClientsFunctions = {
  findClientByPhone: typeof import('./firebase').findClientByPhone
  addNewClient: typeof import('./firebase').addNewClient
  deleteClient: typeof import('./firebase').deleteClient
  getClientsRegisteredOnDateRange: typeof import('./firebase').getClientsRegisteredOnDateRange
  getClientsActiveOnDateRange: typeof import('./firebase').getClientsActiveOnDateRange
  updateBonusForClient: typeof import('./firebase').updateBonusForClient
  getClientBonusValue: typeof import('./firebase').getClientBonusValue
  getClientOperations: typeof import('./firebase').getClientOperations
  updateClient: typeof import('./firebase').updateClient
}

/**
 * Inventory Domain Functions
 * Target: inventory.ts
 */
export type InventoryFunctions = {
  // Supplies
  getSuppliesOnDateRange: typeof import('./firebase').getSuppliesOnDateRange
  saveNewSupply: typeof import('./firebase').saveNewSupply
  updateSupply: typeof import('./firebase').updateSupply
  deleteSupply: typeof import('./firebase').deleteSupply
  // Transfers
  getTransfersOnDateRange: typeof import('./firebase').getTransfersOnDateRange
  saveNewTransfer: typeof import('./firebase').saveNewTransfer
  deleteTransfer: typeof import('./firebase').deleteTransfer
  // WriteOffs
  getWriteOffsOnDateRange: typeof import('./firebase').getWriteOffsOnDateRange
  saveNewWriteOff: typeof import('./firebase').saveNewWriteOff
  deleteWriteOff: typeof import('./firebase').deleteWriteOff
  // Revisions
  getRevisionsOnDateRange: typeof import('./firebase').getRevisionsOnDateRange
  saveNewRevision: typeof import('./firebase').saveNewRevision
  updateRevision: typeof import('./firebase').updateRevision
  deleteRevision: typeof import('./firebase').deleteRevision
}

/* eslint-enable @typescript-eslint/consistent-type-imports */
